import React, { useMemo } from 'react';

import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faChevronDown, faChevronRight, faUniversity } from '@fortawesome/free-solid-svg-icons';

import { formatCurrency } from '../../../utils/formatters';

import {

  computeAccountRequiredFortnightly,

  isRequirementEffectiveForFortnight,

} from '../../../utils/requirementCalculations';

import BankAccountRequirementDetail, { sortRequirements } from './BankAccountRequirementDetail';

import IncludedAllocationsTrigger from './IncludedAllocationsTrigger';

import IncludedBillsTrigger from './IncludedBillsTrigger';
import AccountTitleDisplay from './AccountTitleDisplay';



const BankAccountRequirementCard = ({

  accountKey,

  title,

  requirements,

  fortnightStart,

  fortnightEnd,

  fortnightIndex,

  showInactive,

  isExpanded,

  isMobile,

  onToggle,

  onOpenSheet,

  onEdit,

  onAdvancedGenerate,

  onViewBills,

  onShowInactiveChange,

  renderCategoryIcon,

}) => {

  const requiredFn = useMemo(() => {

    if (!fortnightStart || !fortnightEnd) return 0;

    return computeAccountRequiredFortnightly(requirements, fortnightStart, fortnightEnd);

  }, [requirements, fortnightStart, fortnightEnd]);



  const { allocations, bills } = useMemo(() => {

    const alloc = [];

    const bill = [];

    requirements.forEach((req) => {

      if (req.display_kind === 'allocation') {

        alloc.push(req);

      } else {

        bill.push(req);

      }

    });

    return {

      allocations: sortRequirements(alloc),

      bills: sortRequirements(bill),

    };

  }, [requirements]);



  const activeCount = useMemo(() => {

    if (!fortnightStart || !fortnightEnd) return requirements.length;

    return requirements.filter((r) =>

      isRequirementEffectiveForFortnight(r, fortnightStart, fortnightEnd)

    ).length;

  }, [requirements, fortnightStart, fortnightEnd]);



  const headerId = `bank-req-card-${accountKey}`;

  const panelId = `bank-req-panel-${accountKey}`;



  const handleCardActivate = () => {

    if (isMobile) {

      onOpenSheet(accountKey);

    } else {

      onToggle();

    }

  };



  const handleCardKeyDown = (event) => {

    if (event.key === 'Enter' || event.key === ' ') {

      event.preventDefault();

      handleCardActivate();

    }

  };



  const showQuickView = fortnightStart && fortnightEnd;

  const showAllocationsQuickView = showQuickView && allocations.length > 0;

  const showBillsQuickView = showQuickView && bills.length > 0;



  const inactiveHint =

    showInactive && activeCount < requirements.length ? (

      <span className="text-muted">

        {' '}

        ({activeCount} active)

      </span>

    ) : null;



  const subline = (allocations.length > 0 || bills.length > 0) && (

    <div className="requirements-bank-card-subline">

      {showAllocationsQuickView ? (

        <IncludedAllocationsTrigger

          inline

          allocations={allocations}

          fortnightStart={fortnightStart}

          fortnightEnd={fortnightEnd}

          accountTitle={title}

        />

      ) : (

        allocations.length > 0 && (

          <span>

            {allocations.length} allocation{allocations.length === 1 ? '' : 's'}

          </span>

        )

      )}

      {(allocations.length > 0 && bills.length > 0) && (

        <span className="requirements-subline-sep" aria-hidden="true" />

      )}

      {showBillsQuickView ? (

        <IncludedBillsTrigger

          inline

          bills={bills}

          fortnightStart={fortnightStart}

          fortnightEnd={fortnightEnd}

          accountTitle={title}

        />

      ) : (

        bills.length > 0 && (

          <span className="requirements-subline-pill requirements-subline-pill--static">

            {bills.length} bill{bills.length === 1 ? '' : 's'}

          </span>

        )

      )}

      {inactiveHint}

    </div>

  );



  const cardTap = (

    <div

      className="requirements-bank-card-tap"

      role="button"

      tabIndex={0}

      onClick={handleCardActivate}

      onKeyDown={handleCardKeyDown}

      aria-expanded={isMobile ? undefined : isExpanded}

      aria-controls={isMobile ? undefined : panelId}

      id={headerId}

      aria-label={isMobile ? `Open ${title} breakdown` : `${isExpanded ? 'Collapse' : 'Expand'} ${title}`}

    >

      <div className="requirements-bank-card-tap-inner">

        <FontAwesomeIcon icon={faUniversity} className="requirements-bank-card-icon" />

        <div className="requirements-bank-card-header-main">

          <div className="requirements-bank-card-header-top">

            <AccountTitleDisplay title={title} className="requirements-bank-card-title-row" />

            <div className="requirements-bank-card-hero">

              <span className="requirements-bank-card-amount">{formatCurrency(requiredFn)}</span>

              <span className="requirements-bank-card-amount-suffix text-muted">/fn</span>

            </div>

            <FontAwesomeIcon

              icon={isMobile ? faChevronRight : isExpanded ? faChevronDown : faChevronRight}

              className="requirements-bank-card-chevron"

            />

          </div>

          {subline}

        </div>

      </div>

    </div>

  );



  if (isMobile) {

    return (

      <article className="requirements-bank-card requirements-bank-card--mobile">

        {cardTap}

      </article>

    );

  }



  return (

    <article className={`requirements-bank-card${isExpanded ? ' is-expanded' : ''}`}>

      <div className="requirements-bank-card-header-row">{cardTap}</div>



      {isExpanded && fortnightStart && fortnightEnd && (

        <div className="requirements-bank-card-body" id={panelId} role="region" aria-labelledby={headerId}>

          <BankAccountRequirementDetail

            allocations={allocations}

            bills={bills}

            requirements={requirements}

            fortnightStart={fortnightStart}

            fortnightEnd={fortnightEnd}

            fortnightIndex={fortnightIndex}

            showInactive={showInactive}

            onShowInactiveChange={onShowInactiveChange}

            onEdit={onEdit}

            onAdvancedGenerate={onAdvancedGenerate}

            onViewBills={onViewBills}

            renderCategoryIcon={renderCategoryIcon}

          />

        </div>

      )}

    </article>

  );

};



BankAccountRequirementCard.propTypes = {

  accountKey: PropTypes.string.isRequired,

  title: PropTypes.string.isRequired,

  requirements: PropTypes.arrayOf(PropTypes.object).isRequired,

  fortnightStart: PropTypes.instanceOf(Date),

  fortnightEnd: PropTypes.instanceOf(Date),

  fortnightIndex: PropTypes.number.isRequired,

  showInactive: PropTypes.bool.isRequired,

  isExpanded: PropTypes.bool.isRequired,

  isMobile: PropTypes.bool.isRequired,

  onToggle: PropTypes.func.isRequired,

  onOpenSheet: PropTypes.func.isRequired,

  onEdit: PropTypes.func.isRequired,

  onAdvancedGenerate: PropTypes.func.isRequired,

  onViewBills: PropTypes.func.isRequired,

  onShowInactiveChange: PropTypes.func.isRequired,

  renderCategoryIcon: PropTypes.func,

};



export default BankAccountRequirementCard;

